import { Plus, X } from "lucide-react"
import { Button } from "@/components/ui/button"

// A band's years active or a venue's years open: any number of periods, each
// a start year and an optional end year (#457). Rails nested attributes on
// the server: removing a saved period sends _destroy, a new one is dropped.

export type ActivePeriod = { id: number | null; start_year: number | null; end_year: number | null }
export type ActivePeriodRow = { id: number | null; start_year: string; end_year: string; _destroy?: boolean }

export function periodRows(periods: ActivePeriod[] = []): ActivePeriodRow[] {
  return periods.map((p) => ({ id: p.id, start_year: p.start_year?.toString() ?? "", end_year: p.end_year?.toString() ?? "" }))
}

export function periodAttributes(rows: ActivePeriodRow[]) {
  return rows.map(({ id, start_year, end_year, _destroy }) => (_destroy ? { id, _destroy: "1" } : { id, start_year, end_year }))
}

type Props = {
  legend: string
  hint: string
  rows: ActivePeriodRow[]
  onChange: (rows: ActivePeriodRow[]) => void
  errors?: Record<string, string>
  inputClassName: string
  legendClassName?: string
}

export function ActivePeriodsField({ legend, hint, rows, onChange, errors = {}, inputClassName, legendClassName }: Props) {
  const visible = rows.map((row, index) => ({ row, index })).filter(({ row }) => !row._destroy)
  const messages = Object.entries(errors).filter(([key]) => key.startsWith("active_periods")).map(([, message]) => message)

  function update(index: number, field: "start_year" | "end_year", value: string) {
    onChange(rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)))
  }

  function remove(index: number) {
    const row = rows[index]
    onChange(row.id ? rows.map((r, i) => (i === index ? { ...r, _destroy: true } : r)) : rows.filter((_, i) => i !== index))
  }

  return (
    <fieldset>
      <legend className={legendClassName ?? "block text-sm text-ink-muted mb-1"}>{legend}</legend>
      <div className="space-y-2">
        {visible.map(({ row, index }, position) => (
          <div key={row.id ?? `new-${index}`} className="flex items-center gap-2">
            <input
              id={`period-${position}-start`} name={`period-${position}-start`} type="number" inputMode="numeric" min={1800} max={2100}
              aria-label={`Period ${position + 1}: start year`} placeholder="From"
              value={row.start_year} onChange={(e) => update(index, "start_year", e.target.value)}
              className={`${inputClassName} max-w-[8rem]`}
            />
            <span className="text-sm text-ink-muted">to</span>
            <input
              id={`period-${position}-end`} name={`period-${position}-end`} type="number" inputMode="numeric" min={1800} max={2100}
              aria-label={`Period ${position + 1}: end year`} placeholder="To"
              value={row.end_year} onChange={(e) => update(index, "end_year", e.target.value)}
              className={`${inputClassName} max-w-[8rem]`}
            />
            <Button type="button" variant="ghost" size="icon" onClick={() => remove(index)} aria-label={`Remove period ${position + 1}`} title={`Remove period ${position + 1}`}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
      <Button type="button" variant="ghost" size="sm" className="mt-2" onClick={() => onChange([...rows, { id: null, start_year: "", end_year: "" }])}>
        <Plus className="h-4 w-4" /> Add a period
      </Button>
      <p className="mt-1 text-xs text-ink-muted">{hint}</p>
      {messages.map((message) => <p key={message} className="mt-1 text-xs text-red-600">{message}</p>)}
    </fieldset>
  )
}
